import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { resolveLocalizedPrivacyPath } from "@/utils/publicLocale";
import { stripLocaleFromPathname } from "@/config/publicSiteManifest.js";
import {
  CONSENT_KEY,
  CONSENT_VERSION,
  type CookieConsent,
  readStoredCookieConsent,
  registerCookiePreferenceOpener,
} from "./cookieConsentShared";

const PublicCookiePreferencesPanel = lazy(() => import("./PublicCookiePreferencesPanel"));

const createConsent = (overrides: Partial<CookieConsent>): CookieConsent => ({
  necessary: true,
  analytics: false,
  marketing: false,
  timestamp: Date.now(),
  version: CONSENT_VERSION,
  ...overrides,
});

export const PublicCookieConsentBanner: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookieConsent>(createConsent({}));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const bannerObserverRef = useRef<ResizeObserver | null>(null);

  const bannerRef = useCallback((element: HTMLDivElement | null) => {
    bannerObserverRef.current?.disconnect();
    bannerObserverRef.current = null;

    if (!element) {
      document.documentElement.style.removeProperty("--bottom-chrome-offset");
      return;
    }

    const updateOffset = () => {
      const rect = element.getBoundingClientRect();
      const offset = Math.max(0, window.innerHeight - rect.top);
      document.documentElement.style.setProperty("--bottom-chrome-offset", `${offset}px`);
    };

    updateOffset();
    const observer = new ResizeObserver(updateOffset);
    observer.observe(element);
    bannerObserverRef.current = observer;
  }, []);

  useEffect(
    () => () => {
      bannerObserverRef.current?.disconnect();
      document.documentElement.style.removeProperty("--bottom-chrome-offset");
    },
    [],
  );

  useEffect(() => {
    const consent = readStoredCookieConsent();

    if (!consent) {
      setShowBanner(true);
    } else {
      if (!consent.version || consent.version !== CONSENT_VERSION) {
        setShowBanner(true);
      }

      setPreferences({ ...consent, version: CONSENT_VERSION });
    }

    return registerCookiePreferenceOpener(() => {
      setShowBanner(true);
      setShowPreferences(true);
    });
  }, []);

  useEffect(() => {
    if (!showPreferences) {
      return undefined;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShowPreferences(false);
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [showPreferences]);

  const saveConsent = (consent: CookieConsent) => {
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
      setShowBanner(false);
      setShowPreferences(false);
      setErrorMessage(null);
      window.dispatchEvent(new CustomEvent("cookieConsentChanged", { detail: consent }));
    } catch (error) {
      console.error("Failed to save cookie consent:", error);
      setErrorMessage("Failed to save preferences. Please try again.");
    }
  };

  const acceptAll = () => {
    saveConsent(createConsent({ analytics: true, marketing: true }));
  };

  const acceptNecessary = () => {
    saveConsent(createConsent({ analytics: false, marketing: false }));
  };

  const savePreferences = () => {
    saveConsent(createConsent(preferences));
  };

  const pathname = typeof window !== "undefined" ? window.location.pathname : "/";
  const privacyPath = resolveLocalizedPrivacyPath(pathname);
  const { basePath } = stripLocaleFromPathname(pathname);
  const isPoeticPage = ["/manifesto", "/pledge"].includes(basePath);
  const isPublicLanding = basePath === "/" || basePath === "/urbanismo" || basePath === "/condominios";

  if (!showBanner || isPoeticPage) {
    return null;
  }

  if (isPublicLanding) {
    return (
      <div
        ref={bannerRef}
        className="fixed bottom-6 left-1/2 z-50 w-[calc(100%-3rem)] max-w-sm -translate-x-1/2 md:left-auto md:right-6 md:translate-x-0"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-consent-title"
      >
        {!showPreferences ? (
          <div
            className="border p-4 font-mono text-xs shadow-lg"
            style={{
              backgroundColor: "var(--theme-surface, #111111)",
              color: "var(--theme-ink, #ffffff)",
              borderColor: "var(--theme-line, rgba(255,255,255,0.15))",
            }}
          >
            <p id="cookie-consent-title" className="mb-3 text-[11px] leading-relaxed opacity-60">
              We use cookies to enhance your experience.{" "}
              <a href={privacyPath} className="underline opacity-80 hover:opacity-100">
                Learn more
              </a>
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowPreferences(true)}
                className="px-3 py-1.5 border text-[10px] uppercase tracking-widest opacity-60 transition-colors hover:opacity-100"
                style={{ borderColor: "var(--theme-line, rgba(255,255,255,0.2))" }}
              >
                Customize
              </button>
              <button
                onClick={acceptNecessary}
                className="px-3 py-1.5 border text-[10px] uppercase tracking-widest opacity-60 transition-colors hover:opacity-100"
                style={{ borderColor: "var(--theme-line, rgba(255,255,255,0.2))" }}
              >
                Essential
              </button>
              <button
                onClick={acceptAll}
                className="px-3 py-1.5 bg-[#FF3300] text-[10px] uppercase tracking-widest text-black transition-colors hover:bg-[#FF3300]/90"
              >
                Accept
              </button>
            </div>
          </div>
        ) : (
          <Suspense fallback={null}>
            <PublicCookiePreferencesPanel
              variant="landing"
              preferences={preferences}
              onClose={() => {
                setShowPreferences(false);
                setErrorMessage(null);
              }}
              onSave={savePreferences}
              onAnalyticsChange={(checked) => setPreferences((current) => ({ ...current, analytics: checked }))}
              onMarketingChange={(checked) => setPreferences((current) => ({ ...current, marketing: checked }))}
              errorMessage={errorMessage}
            />
          </Suspense>
        )}
      </div>
    );
  }

  return (
    <div
      ref={bannerRef}
      className="fixed bottom-0 left-0 right-0 z-50 border-t bg-background/95 p-3 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/80 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-consent-title"
    >
      <div className="mx-auto max-w-5xl rounded-xl border bg-background p-4 sm:p-6">
        {!showPreferences ? (
          <div className="flex flex-col gap-4 md:flex-row md:items-center">
            <div className="flex-1">
              <h3 id="cookie-consent-title" className="mb-2 text-base font-semibold sm:text-lg">
                We value your privacy
              </h3>
              <p className="text-xs text-muted-foreground sm:text-sm">
                We use cookies to enhance your experience, analyze site usage, and personalize content. You can choose
                which cookies you accept.{" "}
                <a href={privacyPath} className="underline hover:text-primary">
                  Learn more
                </a>
              </p>
            </div>

            <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row">
              <button
                type="button"
                onClick={() => setShowPreferences(true)}
                className="inline-flex min-h-[44px] items-center justify-center rounded-md border px-4 py-2 text-sm font-medium transition-colors hover:bg-muted"
              >
                Customize
              </button>
              <button
                type="button"
                onClick={acceptNecessary}
                className="inline-flex min-h-[44px] items-center justify-center rounded-md border px-4 py-2 text-sm font-medium transition-colors hover:bg-muted"
              >
                Essential Only
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="inline-flex min-h-[44px] items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              >
                Accept All
              </button>
            </div>
          </div>
        ) : (
          <Suspense fallback={null}>
            <PublicCookiePreferencesPanel
              variant="default"
              preferences={preferences}
              onClose={() => {
                setShowPreferences(false);
                setErrorMessage(null);
              }}
              onSave={savePreferences}
              onAnalyticsChange={(checked) => setPreferences((current) => ({ ...current, analytics: checked }))}
              onMarketingChange={(checked) => setPreferences((current) => ({ ...current, marketing: checked }))}
              errorMessage={errorMessage}
            />
          </Suspense>
        )}
      </div>
    </div>
  );
};
